/**
 * Technologist QC workbench — impedance heatmap + pass/fail sign-off (Epic B3).
 */

import React from "react";
import { Alert, Badge, Button, Card, Col, Container, Row, Table } from "react-bootstrap";
import {
  canPromoteToPhysicianQueue,
} from "../utils/techQcWorkflow";
import { usePortalWorkbenchSnapshot } from "../services/portalWorkbenchApi";

export default function TechQcWorkbenchPanel() {
  const { snapshot, loading, error, reload } = usePortalWorkbenchSnapshot();
  const techQc = snapshot?.techQc;
  const canPromote = canPromoteToPhysicianQueue({
    status: techQc?.unresolvedBadChannels ? "rejected" : "signed",
    technologistId: "system",
    signedAtIso: snapshot?.generatedAtIso ?? new Date(0).toISOString(),
    notes: null,
    schemaVersion: "portal-workbench-v1",
    blocksPhysicianReporting: Boolean(techQc?.unresolvedBadChannels),
  });

  return (
    <Container fluid className="py-3" data-testid="tech-qc-workbench">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="h4 mb-0">Technologist signal QC</h2>
        <Button size="sm" variant="outline-primary" onClick={reload} disabled={loading}>
          Refresh
        </Button>
      </div>
      <Alert variant="secondary" className="small">
        <div>
          <strong>Intended use:</strong> Signal-quality review support from recorded QC events and processing logs.
        </div>
        <div className="text-muted mt-1">Original EEG samples remain the source record for clinical interpretation.</div>
      </Alert>
      {error ? <Alert variant="danger">{error}</Alert> : null}
      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Bad / poor channels</div>
              <div className="fs-4">{techQc?.unresolvedBadChannels ?? 0}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="text-muted small">Physician queue</div>
              <div className="fs-5">
                {canPromote ? (
                  <Badge bg="success">Cleared</Badge>
                ) : (
                  <Badge bg="danger">Blocked</Badge>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <div className="text-muted small">QC status</div>
              <div className="fs-5">
                <Badge bg="secondary">{canPromote ? "signed" : "needs review"}</Badge>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Card className="mb-3">
        <Card.Header>Electrode impedance heatmap</Card.Header>
        <Card.Body className="p-0">
          <Table size="sm" className="mb-0" data-testid="tech-qc-heatmap">
            <thead>
              <tr>
                <th>Channel</th>
                <th>Status</th>
                <th>Basis</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3}>Loading QC evidence...</td></tr>
              ) : (techQc?.poorChannelNames.length ?? 0) === 0 ? (
                <tr><td colSpan={3}>No unresolved poor-channel evidence in the database.</td></tr>
              ) : (
                techQc?.poorChannelNames.map((channel) => (
                  <tr key={channel}>
                    <td>{channel}</td>
                    <td><Badge bg="warning">review</Badge></td>
                    <td className="small">Artifact or quality annotation references this channel.</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
      <Card>
        <Card.Header>Recent processing evidence</Card.Header>
        <Table size="sm" responsive className="mb-0">
          <thead>
            <tr>
              <th>Operation</th>
              <th>Status</th>
              <th>Recording / patient</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4}>Loading processing evidence...</td>
              </tr>
            ) : (techQc?.latestLogs ?? []).length === 0 ? (
              <tr>
                <td colSpan={4}>No recent processing logs in the database.</td>
              </tr>
            ) : (
              (techQc?.latestLogs ?? []).map((log) => (
                <tr key={log.log_id}>
                  <td>{log.operation_type}</td>
                  <td>
                    <Badge
                      bg={
                        log.operation_status === "completed"
                          ? "success"
                          : "secondary"
                      }
                    >
                      {log.operation_status}
                    </Badge>
                  </td>
                  <td className="small">
                    <div>{log.filename || log.evidence_label || "Recording"}</div>
                    {log.patient_label || log.study_label ? (
                      <div className="text-muted">
                        {[log.patient_label, log.study_label]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    ) : null}
                  </td>
                  <td className="small text-muted">
                    {log.error_message || "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
    </Container>
  );
}
